import * as React from "react";
import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import { NoSearchResult, PaginatedList, Spinner } from "components/common";
import { GLCheckbox } from "common/components";
import { Input, MenuItem } from "components/bootstrap";
import useDebouncedValue from "hooks/useDebouncedValue";
export const PAGE_SIZE = 10;
const sidePaddings = ({ theme }) => css`
  padding: 0 ${theme.spacings.sm};
`;
const PaddingContainer = styled.div(sidePaddings);
const StyledPaginatedList = styled(PaginatedList)(sidePaddings);
const SearchInput = styled(Input)`
  margin-bottom: 6px;
`;
const ScrollableArea = styled.div`
  max-height: 50vh;
  overflow: auto;
`;
const FilterItem = styled.div(
  ({ theme }) => css`
    cursor: pointer;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;

    font-weight: normal;
    color: ${theme.colors.text.primary};
  `
);
const ColumnFilterOptions = ({
  filterBy,
  filterData,
  loadOptions = void 0,
  loadOptionsKey = "",
  isSelected,
  onToggle
}) => {
  const { t } = useTranslation("enterprise_security_app");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [debouncedQuery] = useDebouncedValue(query, 250);
  const { data, isLoading } = useQuery({
    queryKey: ["column-filter-options", filterBy, loadOptionsKey, debouncedQuery, page],
    queryFn: () => loadOptions({ query: debouncedQuery, page, perPage: PAGE_SIZE }),
    enabled: !!loadOptions,
    placeholderData: keepPreviousData
  });
  const needle = debouncedQuery.trim().toLowerCase();
  const localMatches = loadOptions ? [] : filterData.filter(({ value }) => !!value).filter(({ label }) => !needle || label.toLowerCase().includes(needle));
  const options = loadOptions ? (data == null ? void 0 : data.options) || [] : localMatches.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const total = loadOptions ? (data == null ? void 0 : data.total) || 0 : localMatches.length;
  const showSearch = !!loadOptions || filterData.length > PAGE_SIZE;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, showSearch && /* @__PURE__ */ React.createElement(PaddingContainer, null, /* @__PURE__ */ React.createElement(
    SearchInput,
    {
      type: "text",
      id: `filter-${filterBy}-search`,
      formGroupClassName: "",
      value: query,
      placeholder: t("column_filter_options.search_placeholder", { name: filterBy.replaceAll("_", " ") }),
      "aria-label": `filter-${filterBy}-search`,
      onChange: ({ target: { value } }) => {
        setQuery(value);
        setPage(1);
      },
      onKeyDown: (e) => e.stopPropagation()
    }
  ), isLoading && /* @__PURE__ */ React.createElement(Spinner, null)), options.length > 0 && /* @__PURE__ */ React.createElement(
    StyledPaginatedList,
    {
      showPageSizeSelect: false,
      totalItems: total,
      hidePreviousAndNextPageLinks: true,
      hideFirstAndLastPageLinks: true,
      activePage: page,
      pageSize: PAGE_SIZE,
      onChange: (newPage) => setPage(newPage),
      useQueryParameter: false
    },
    /* @__PURE__ */ React.createElement(ScrollableArea, null, options.map((fData) => /* @__PURE__ */ React.createElement(MenuItem, { key: `filter-${filterBy}-${fData.value}`, onClick: onToggle(fData) }, /* @__PURE__ */ React.createElement(FilterItem, null, /* @__PURE__ */ React.createElement(
      GLCheckbox,
      {
        onChange: onToggle(fData),
        checked: isSelected(fData),
        "aria-label": `filter-${fData.label}`
      }
    ), /* @__PURE__ */ React.createElement("span", { tabIndex: 0, role: "checkbox", "aria-checked": isSelected(fData), title: `filter-${fData.label}` }, fData.label)))))
  ), !isLoading && options.length === 0 && /* @__PURE__ */ React.createElement(PaddingContainer, null, /* @__PURE__ */ React.createElement(NoSearchResult, null, t("column_filter_options.no_values"))));
};
export default ColumnFilterOptions;
